import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getAuthToken } from "jwt-storage-lib";
import { Company } from "@builder/types/Company.type";
import { OnboardCompanyBody } from "@builder/types/OnboardCompany.type";

export const companiesApi = createApi({
    reducerPath: "companiesApi",
    baseQuery: fetchBaseQuery({
        baseUrl: import.meta.env.VITE_URL_HUB_API,
        prepareHeaders: async headers => {
            const token = await getAuthToken();

            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }

            return headers;
        }
    }),
    tagTypes: ["companies"],
    endpoints: builder => ({
        getCompany: builder.query<Company, string>({
            query: companyID => `company/${companyID}`,
            providesTags: ["companies"]
        }),
        getCompaniesByOrg: builder.query<Company[], string>({
            query: companyId => `company/builder/by-org/${companyId}?fullCompany=true&&with-demo-companies=true`,
            providesTags: ["companies"]
        }),
        renameCompany: builder.mutation<Company, { companyId: string; newName: string }>({
            query: ({ companyId, newName }) => ({
                url: `company/${companyId}`, // CompanyController@PATCH
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: { name: newName }
            }),
            invalidatesTags: ["companies"]
        }),
        // added a second query since it needs to be executed after the company is renamed and have a different loading state.
        // There is probably a better way to do this - it's a quickfix
        updateOnboardingComplete: builder.mutation<Company, { companyId: string; registrationCompleted: boolean }>({
            query: ({ companyId, registrationCompleted }) => ({
                url: `company/${companyId}`,
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: { registrationCompleted }
            }),
            invalidatesTags: ["companies"]
        }),
        onboardCompany: builder.mutation<Company, { organizationId: string; onboardCompanyData: Partial<OnboardCompanyBody> }>({
            query: ({ organizationId, onboardCompanyData }) => ({
                url: `cdk/company-management/onboard-company/${organizationId}`,
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: onboardCompanyData
            })
        }),
        updateCompanyLogo: builder.mutation<Company, { companyId: string; logoUrl: string }>({
            query: ({ companyId, logoUrl }) => ({
                url: `company/${companyId}`, // CompanyController@PATCH
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: { logo: logoUrl }
            }),
            invalidatesTags: ["companies"]
        }),
        updateCompany: builder.mutation<Company, { companyId: string; company: Partial<Company> }>({
            query: ({ companyId, company }) => ({
                url: `company/${companyId}`, // CompanyController@PATCH
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: company
            }),
            invalidatesTags: ["companies"]
        })
    })
});

export const {
    useGetCompanyQuery,
    useGetCompaniesByOrgQuery,
    useRenameCompanyMutation,
    useUpdateCompanyLogoMutation,
    useUpdateOnboardingCompleteMutation,
    useOnboardCompanyMutation,
    useUpdateCompanyMutation
} = companiesApi;
